import type { State } from "@shared/types/graphqlGenerated";
type AddressComponent = google.maps.GeocoderAddressComponent;

interface GoogleAddress {
  streetAddress: string;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  latitude?: number;
  longitude?: number;
}

const selectState = (stateAddr: string, states: State[]) => {
  const foundState = states?.find(
    (state: State) => stateAddr.toUpperCase() === state.abbr.toUpperCase()
  );

  return foundState ? foundState.abbr : null;
};

const isStreetNumberType = ({ types }: AddressComponent) => types.includes("street_number");
const getStreetNumber = (components: AddressComponent[]): string | null => {
  const streetNumber = components.find(isStreetNumberType);
  if (streetNumber) {
    return streetNumber.long_name;
  }

  return null;
};

// Backend `Spree::Address` validates address1 at a maximum of 35 characters.
// Keep this in sync with that validation so autocomplete never emits a value
// the server will reject.
const MAX_STREET_ADDRESS_LENGTH = 35;

const isRouteType = ({ types }: AddressComponent) => types.includes("route");
const getAddress = (components: AddressComponent[]): string => {
  const route = components.find(isRouteType);
  if (!route) {
    return "";
  }

  const streetNumber = getStreetNumber(components);
  const buildStreetAddress = (routeName: string): string =>
    streetNumber ? `${streetNumber} ${routeName}`.trim() : routeName.trim();

  const streetAddress = buildStreetAddress(route.long_name);

  // Google returns the fully spelled-out street type in `long_name`
  // (e.g. "Boulevard Northwest"), which can exceed the 35-char cap. Fall back
  // to the abbreviated `short_name` (e.g. "Blvd NW") when the long form is too
  // long so the address stays saveable.
  if (streetAddress.length > MAX_STREET_ADDRESS_LENGTH && route.short_name) {
    return buildStreetAddress(route.short_name);
  }

  return streetAddress;
};

// Priority order for determining a "city-like" component.
// Higher priority types appear first.
// We intentionally exclude administrative_area_level_2 (usually a county)
// because it is not always a city, and use neighborhood only as a fallback.
const CITY_TYPES_PRIORITY_ORDER = [
  "locality", // True city/town
  "sublocality", // Subdivision of a city
  "sublocality_level_1", // More granular sublocality
  "sublocality_level_2", // Even smaller area
  "administrative_area_level_3", // Small administrative division (sometimes city-like)
  "neighborhood", // Only fallback if no higher-priority types exist
] as const;

// Precompute a set for fast "is city-type" checks
const CITY_TYPES_SET = new Set<string>(CITY_TYPES_PRIORITY_ORDER);

/**
 * Checks if a given AddressComponent has a type considered "city-like".
 *
 * @param types - the types array of an AddressComponent
 * @returns true if it matches any city-like type
 */
const isCityType = ({ types }: AddressComponent): boolean =>
  types.some(type => CITY_TYPES_SET.has(type));

/**
 * Finds the most relevant city-like component from a list of AddressComponents,
 * using the priority order defined above.
 * Excludes components that have excluded types (e.g., counties, postal codes).
 *
 * @param components - list of AddressComponents to search
 * @param excludedTypes - set of types to exclude from selection
 * @returns the first matching component by priority, or undefined if none found
 */
const findCityComponentByPriority = (
  components: AddressComponent[],
  excludedTypes: Set<string> = EXCLUDED_FALLBACK_TYPES
): AddressComponent | undefined => {
  for (const cityType of CITY_TYPES_PRIORITY_ORDER) {
    const component = components.find(
      c => c.types.includes(cityType) && !c.types.some(type => excludedTypes.has(type))
    );
    if (component) return component;
  }

  return undefined; // No city-like component found
};

// Module-level constants for political fallback filtering
// Only allow neighborhood as a last-resort fallback when no locality exists
const ALLOWED_FALLBACK_TYPES = new Set(["neighborhood"]);
// Types that should exclude a component from being considered a city, even if it has a valid city type
// Note: neighborhood is NOT in this list - it's a valid city type, but components with neighborhood
// AND any of these excluded types should be filtered out
const EXCLUDED_FALLBACK_TYPES = new Set([
  "administrative_area_level_2", // not county or division
  "postal_code", // not a postal code
  "premise", // not a named building
  "subpremise", // not an unit or suite
  "administrative_area_level_1", // not a state
  "country", // not a country
  "street_number", // not a street number
  "route", // not a route
]);

const getCity = (components: AddressComponent[]): string | null => {
  // Try primary city types first with priority order
  // Exclude components that have excluded types (counties, postal codes, etc.)
  let cityComponent = findCityComponentByPriority(components, EXCLUDED_FALLBACK_TYPES);

  // If no city found, try fallback: look for neighborhood components
  // This handles cases where Google doesn't classify it as a standard city type
  // We only use neighborhood as a last-resort fallback when no locality exists
  if (!cityComponent) {
    const fallbackComponent = components.find(
      c =>
        c.types.some(type => ALLOWED_FALLBACK_TYPES.has(type)) &&
        !c.types.some(type => EXCLUDED_FALLBACK_TYPES.has(type))
    );

    if (fallbackComponent) {
      cityComponent = fallbackComponent;
    }
  }

  if (!cityComponent) {
    return null;
  }

  return cityComponent.long_name;
};

const isStateType = ({ types }: AddressComponent) => types.includes("administrative_area_level_1");
const getRegion = (components: AddressComponent[], states: State[]): string | null => {
  const stateComponent = components.find(isStateType);
  if (!stateComponent) {
    return null;
  }

  // Try to find the state in the states array first (for validation)
  const validatedState = selectState(stateComponent.short_name, states);
  if (validatedState) {
    return validatedState;
  }

  // If not found in states array, validate that it looks like a valid US state abbreviation
  // (2 uppercase letters) before returning it
  const stateAbbr = stateComponent.short_name;
  if (stateAbbr && /^[A-Z]{2}$/.test(stateAbbr)) {
    return stateAbbr;
  }

  return null;
};

const isZipType = ({ types }: AddressComponent) => types.includes("postal_code");
const getPostalCode = (components: AddressComponent[]): string | null => {
  const zipComponent = components.find(isZipType);
  if (!zipComponent) {
    return null;
  }

  return zipComponent.long_name;
};

const getLatLong = (placeDetails: google.maps.GeocoderResult) => {
  if (!placeDetails?.geometry?.location) {
    return {
      latitude: undefined,
      longitude: undefined,
    };
  }

  return {
    latitude: placeDetails.geometry.location.lat?.(),
    longitude: placeDetails.geometry.location.lng?.(),
  };
};

const GoogleAddress = (
  placeDetails: google.maps.GeocoderResult[],
  selectedStreetNumber: string,
  states: State[],
  includeLatLng?: boolean
): GoogleAddress => {
  const addressComponents = placeDetails[0].address_components;
  const formattedAddress = placeDetails[0].formatted_address;

  const streetAddress = getAddress(addressComponents) || selectedStreetNumber;
  let city = getCity(addressComponents);

  // Fallback: If city is still null, try to extract from formatted address
  // Format is typically: "Street, City, State ZIP, Country"
  if (!city && formattedAddress) {
    const parts = formattedAddress.split(",").map(p => p.trim());

    // Usually: [street, city, state zip, country]
    // For "14615 Becurtesy Court, Cypress, TX 77429, USA"
    // parts[0] = street, parts[1] = city, parts[2] = state zip
    if (parts.length >= 2) {
      const potentialCity = parts[1];

      // Verify it's not a state abbreviation (2-3 letters) and not empty
      // Also check if it contains state/zip pattern (e.g., "TX 77429")
      const isStateZipPattern = /^[A-Z]{2}\s+\d{5}(-\d{4})?$/.test(potentialCity);

      if (potentialCity?.length > 3 && !isStateZipPattern) {
        city = potentialCity;
      } else if (parts.length >= 3) {
        // Sometimes the format might be different, try parts[2] and extract city before state/zip
        const thirdPart = parts[2];
        // Extract city from "State ZIP" format (no comma since parts was split by comma)
        const cityMatch = thirdPart?.match(/^([^,]+)\s+[A-Z]{2}\s+\d{5}/);
        if (cityMatch && cityMatch[1]) {
          const extractedCity = cityMatch[1].trim();
          if (extractedCity.length > 3) {
            city = extractedCity;
          }
        }
      }
    }
  }

  const region = getRegion(addressComponents, states);
  const postalCode = getPostalCode(addressComponents);

  if (includeLatLng && typeof includeLatLng === "boolean") {
    const { latitude, longitude } = getLatLong(placeDetails[0]);

    return { streetAddress, city, region, postalCode, latitude, longitude };
  }

  return { streetAddress, city, region, postalCode };
};

export {
  GoogleAddress,
  selectState,
  getStreetNumber,
  getAddress,
  isCityType,
  getCity,
  getRegion,
  getPostalCode,
};
